import { useState } from "react";
import { acceptAmount, acceptWhole } from "../lib/money";
import { Alert, Button, Field, Input, Select, cx } from "../components/ui";
import { money } from "../lib/money";
import { describeOffer } from "../lib/promotions";
import { ApiError } from "../lib/api";
import {
  useCreatePromoCampaign,
  useGeneratePromoCode,
  useUpdatePromoCampaign,
} from "../lib/queries";
import type { PromoCampaign, PromoCampaignInput } from "../lib/types";

/**
 * Configuring a campaign.
 *
 * Amounts are typed in currency and sent in micros, because a promotion is one
 * of the few forms on this console where a slipped decimal is a five-figure
 * mistake. The conversion happens here rather than in the API so that every
 * money field crossing the wire has one unit.
 *
 * `code` and `kind` are fixed once created. Changing a code orphans every link
 * that carries it while leaving redemptions attached to a name nobody
 * recognises; changing a kind reinterprets the columns existing redemptions were
 * paid against. Both are a new campaign, and the API refuses them too.
 */
/**
 * Applies a field guard, or drops the keystroke.
 *
 * These fields set money and counts an operator is trusted with — a bonus rate,
 * a cap that bounds the platform's liability on a match, the credit a code
 * grants. `type="number"` let a scroll change any of them without a click.
 */
function accept(next: string, set: (value: string) => void): void {
  const value = acceptAmount(next);
  if (value !== null) set(value);
}

function acceptCount(next: string, set: (value: string) => void): void {
  const value = acceptWhole(next);
  if (value !== null) set(value);
}

export function PromoCampaignForm({
  campaign,
  onDone,
}: {
  campaign: PromoCampaign | null;
  onDone: () => void;
}) {
  const create = useCreatePromoCampaign();
  const update = useUpdatePromoCampaign();
  const generate = useGeneratePromoCode();

  const [kind, setKind] = useState<"grant" | "match">(campaign?.kind ?? "grant");
  const [code, setCode] = useState(campaign?.code ?? "");
  const [name, setName] = useState(campaign?.name ?? "");
  const [grant, setGrant] = useState(units(campaign?.grantMicros));
  const [rate, setRate] = useState(campaign?.matchBps ? String(campaign.matchBps / 100) : "50");
  const [cap, setCap] = useState(units(campaign?.matchCapMicros));
  const [minTopUp, setMinTopUp] = useState(units(campaign?.matchMinTopUpMicros));
  const [ttlDays, setTtlDays] = useState(
    campaign?.creditTtlDays === undefined ? "60" : (campaign?.creditTtlDays?.toString() ?? ""),
  );
  const [endsAt, setEndsAt] = useState(campaign?.endsAt?.slice(0, 10) ?? "");
  const [maxRedemptions, setMaxRedemptions] = useState(
    campaign?.maxRedemptions?.toString() ?? "",
  );
  const [newMembersOnly, setNewMembersOnly] = useState(campaign?.newMembersOnly ?? true);
  const [featuredOnWeb, setFeaturedOnWeb] = useState(campaign?.featuredOnWeb ?? false);
  const [featuredForAffiliates, setFeaturedForAffiliates] = useState(
    campaign?.featuredForAffiliates ?? false,
  );
  const [aiSpendable, setAiSpendable] = useState(campaign?.spendableOn === "all");
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  /**
   * Fills the code field with one the API has confirmed is free.
   *
   * Overwrites whatever is there, which is what a Generate button should do —
   * somebody who presses it has decided not to name the campaign themselves.
   * A failure surfaces in the form's own error line rather than silently leaving
   * the field alone, because an unchanged field reads as a button that does
   * nothing.
   */
  async function fillGeneratedCode() {
    setError(null);
    try {
      const result = await generate.mutateAsync();
      setCode(result.code);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not generate a code.");
    }
  }

  /*
   * The offer as currently typed, in the shape the list renders from.
   *
   * Built from the raw inputs on every keystroke rather than from a submitted
   * payload, so the preview below tracks the form instead of lagging it. Shared
   * with the list through `describeOffer`, which is what stops the form
   * promising something the list contradicts afterwards.
   */
  const preview = {
    kind,
    grantMicros: kind === "grant" ? micros(grant) : null,
    matchBps: kind === "match" ? Math.round(Number(rate || 0) * 100) : null,
    matchCapMicros: kind === "match" ? micros(cap) : null,
    matchMinTopUpMicros: kind === "match" && minTopUp.trim() !== "" ? micros(minTopUp) : null,
    creditTtlDays: aiSpendable || ttlDays.trim() === "" ? null : Number(ttlDays),
  };

  /**
   * The most this campaign could cost, as typed. Null is unbounded.
   *
   * Deliberately not `remainingExposureMicros` from lib/promotions: that one
   * reasons about a *saved* campaign, netting off what has already been claimed.
   * This is the commitment being created, so it is the full quota — the figure
   * somebody should see before agreeing to it, not the figure that is left.
   */
  const exposure = (() => {
    const per = kind === "grant" ? micros(grant) : micros(cap);
    if (!Number.isFinite(per) || per <= 0) return 0;
    if (maxRedemptions.trim() === "") return null;
    const slots = Number(maxRedemptions);
    if (!Number.isFinite(slots) || slots <= 0) return 0;
    return per * slots;
  })();

  async function submit() {
    setError(null);

    const payload: PromoCampaignInput = {
      name: name.trim(),
      newMembersOnly,
      // Grants only. A match campaign cannot be advertised as signup credit, and
      // the API filters to grants regardless, so sending true for one would be a
      // flag that reads as set and does nothing.
      featuredOnWeb: kind === "grant" ? featuredOnWeb : false,
      /*
       * Unrestricted by kind, unlike the flag above, and the difference is the
       * point. A match campaign cannot be advertised as signup credit on a
       * marketing page, but it is a *better* thing to give an affiliate than a
       * grant: it pays on the referred customer's first top-up, which is the
       * same event their own commission fires on.
       */
      featuredForAffiliates,
      spendableOn: aiSpendable ? "all" : "workloads",
      /*
       * Empty means "no limit", which the API reads as null. Distinguished from
       * absent, because clearing a quota an operator previously set is a real
       * edit and `coalesce` cannot express it.
       */
      creditTtlDays: aiSpendable || ttlDays.trim() === "" ? null : Number(ttlDays),
      maxRedemptions: maxRedemptions.trim() === "" ? null : Number(maxRedemptions),
      // Read as the end of the chosen day rather than its start, so a campaign
      // set to end on the 30th is redeemable on the 30th.
      endsAt: endsAt === "" ? null : new Date(`${endsAt}T23:59:59Z`).toISOString(),
    };

    if (kind === "grant") {
      payload.grantMicros = micros(grant);
      payload.matchBps = null;
      payload.matchCapMicros = null;
      payload.matchMinTopUpMicros = null;
    } else {
      payload.grantMicros = null;
      payload.matchBps = Math.round(Number(rate) * 100);
      payload.matchCapMicros = micros(cap);
      payload.matchMinTopUpMicros = minTopUp.trim() === "" ? null : micros(minTopUp);
    }

    try {
      if (campaign) {
        await update.mutateAsync({ id: campaign.id, ...payload });
      } else {
        await create.mutateAsync({
          ...payload,
          code: code.trim(),
          kind,
          // Never on creation. A campaign is written, read back, and then
          // switched on from the list — the alternative is a live code the
          // instant a half-filled form is submitted.
          active: false,
        });
      }
      onDone();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save that campaign.");
    }
  }

  return (
    <div className="space-y-4">
      {error && <Alert>{error}</Alert>}

      {!campaign && (
        <div className="grid gap-4 sm:grid-cols-2">
          {/*
            Generate sits in the label row rather than beside the input, so the
            field keeps its full width — a code is the one value here somebody
            reads back character by character.
          */}
          <Field
            label="Code"
            htmlFor="promo-code"
            hint="Case-insensitive."
            action={
              <button
                type="button"
                className="text-xs text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover disabled:text-fg-subtle"
                disabled={generate.isPending}
                onClick={() => void fillGeneratedCode()}
              >
                {generate.isPending ? "Generating…" : "Generate"}
              </button>
            }
          >
            <Input
              id="promo-code"
              value={code}
              placeholder="TRY300"
              className="font-mono uppercase"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Field label="Kind" htmlFor="promo-kind">
            <Select
              id="promo-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as "grant" | "match")}
            >
              <option value="grant">Grant</option>
              <option value="match">Match</option>
            </Select>
          </Field>
        </div>
      )}

      <Field label="Name" htmlFor="promo-name" hint="Shown on the ledger.">
        <Input
          id="promo-name"
          value={name}
          placeholder="Try Maxlayer"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      {kind === "grant" ? (
        <Field label="Credit" htmlFor="promo-grant" hint="Per redemption.">
          <Input
            id="promo-grant"
            inputMode="decimal"
            value={grant}
            placeholder="300.00"
            onChange={(e) => accept(e.target.value, setGrant)}
          />
        </Field>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Bonus %" htmlFor="promo-rate" hint="100 doubles.">
            <Input
              id="promo-rate"
              inputMode="numeric"
              value={rate}
              onChange={(e) => acceptCount(e.target.value, setRate)}
            />
          </Field>
          {/* Required with the rate: "double your top-up" without a cap is an
              unbounded liability on a $10,000 payment. */}
          <Field label="Cap" htmlFor="promo-cap" hint="Max per match.">
            <Input
              id="promo-cap"
              inputMode="decimal"
              value={cap}
              placeholder="50.00"
              onChange={(e) => accept(e.target.value, setCap)}
            />
          </Field>
          <Field label="Minimum top-up" htmlFor="promo-min" hint="Blank = any.">
            <Input
              id="promo-min"
              inputMode="decimal"
              value={minTopUp}
              placeholder="100.00"
              onChange={(e) => accept(e.target.value, setMinTopUp)}
            />
          </Field>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        {/*
          Disabled rather than merely refused, because the reason is not
          guessable from the field.

          Nothing records what was spent from an AI-spendable lot — allocations
          are written by the workload meter, and inference does not touch lots —
          so an expiry would forfeit the whole grant, including tokens the
          customer had already bought. The API refuses the combination and a
          check constraint makes it impossible; this is what stops an operator
          filling in a number and being told no afterwards.
        */}
        <Field
          label="Credit expires after"
          htmlFor="promo-ttl"
          hint={aiSpendable ? "Credit that pays for AI does not expire." : "Days. Blank = never."}
        >
          <Input
            id="promo-ttl"
            inputMode="numeric"
            value={aiSpendable ? "" : ttlDays}
            disabled={aiSpendable}
            placeholder={aiSpendable ? "Never" : "60"}
            onChange={(e) => acceptCount(e.target.value, setTtlDays)}
          />
        </Field>
        <Field label="Offer closes" htmlFor="promo-ends" hint="Blank = open.">
          <Input
            id="promo-ends"
            type="date"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </Field>
        <Field label="Total redemptions" htmlFor="promo-max" hint="Blank = unlimited.">
          <Input
            id="promo-max"
            inputMode="numeric"
            value={maxRedemptions}
            placeholder="1000"
            onChange={(e) => acceptCount(e.target.value, setMaxRedemptions)}
          />
        </Field>
      </div>

      <label className="flex items-start gap-2.5 text-sm text-fg">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={newMembersOnly}
          onChange={(e) => setNewMembersOnly(e.target.checked)}
        />
        <span>
          New customers only
          <span className="block text-xs text-fg-subtle">Refused once an org has paid.</span>
        </span>
      </label>

      {/*
        Only for a grant, because only a grant can be advertised honestly. A
        match pays nothing until somebody tops up, so "sign up and get credit"
        is not true of one — and the public endpoint filters to grants anyway.
        Hiding the control is better than offering one that silently does
        nothing.

        Worded as what it does rather than as a status. "Feature on the website"
        sounds like a display preference; this publishes the code to anybody who
        loads a public page, which is a different decision from running the
        campaign at all. That is the whole reason it is a separate flag — see
        migration 0087.
      */}
      {kind === "grant" && (
        <label className="flex items-start gap-2.5 text-sm text-fg">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={featuredOnWeb}
            onChange={(e) => setFeaturedOnWeb(e.target.checked)}
          />
          <span>
            Publish this code on the website
            <span className="block text-xs text-fg-subtle">
              Shows it on /campaign/try-max, where anyone can claim it. One campaign at a time.
            </span>
          </span>
        </label>
      )}

      {/*
        A second, separate release — and it has to be separate.

        The flag above is capped at one campaign platform-wide and is grant-only
        on the way out, because the marketing page shows a single offer and its
        copy says "sign up and get credit". Neither limit suits affiliates:
        several offers can be live for them to choose between, and a match
        campaign is the better one to hand over.

        Both default off. A code here is not merely visible — it is given to
        people whose job is publishing codes, which is a larger exposure than
        the website's and deserves its own decision rather than riding on one
        made for a different surface.
      */}
      <label className="flex items-start gap-2.5 text-sm text-fg">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={featuredForAffiliates}
          onChange={(e) => setFeaturedForAffiliates(e.target.checked)}
        />
        <span>
          Give this code to affiliates
          <span className="block text-xs text-fg-subtle">
            Shows it on the affiliate dashboard for them to share. Any number of campaigns, and
            either kind.
          </span>
        </span>
      </label>

      {/*
        The one setting on this form that decides what the credit can be turned
        into, rather than who gets it or how much.

        Off by default and worded as a consequence rather than a category,
        because both halves of it surprise people. Promotional credit normally
        pays for apps and databases only — capacity the platform has already
        bought — while every inference call is bought again, per request, from a
        supplier, so credit spent on tokens is cash leaving the same day. And
        turning it on opens AI for these customers without a payment, which is
        otherwise the one thing that does.
      */}
      <label className="flex items-start gap-2.5 text-sm text-fg">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={aiSpendable}
          onChange={(e) => setAiSpendable(e.target.checked)}
        />
        <span>
          Let this credit pay for AI inference
          <span className="block text-xs text-fg-subtle">
            Off by default: promotional credit normally covers apps and databases only. Turning it
            on also opens AI for these customers without a top-up, and every call is bought from a
            supplier on the day it is made.
          </span>
        </span>
      </label>

      {/*
        What was just typed, read back as an offer and as a commitment.

        The preview is not decoration. A grant amount and a quota are two
        innocuous fields whose product is the number that matters, and nobody
        multiplies before clicking Create — so the form does it, next to the
        inputs, while there is still time to change one.
      */}
      <div className="space-y-1.5 rounded-md border border-line bg-raised px-4 py-3">
        <p className="text-sm text-fg">{describeOffer(preview, money)}</p>
        <p
          className={cx(
            "text-sm",
            exposure === null || exposure > 0 ? "text-warn" : "text-fg-subtle",
          )}
        >
          {exposure === null
            ? "Uncapped — no redemption limit."
            : exposure > 0
              ? `Up to ${money(exposure)} total.`
              : "Set an amount and a limit to see the maximum."}
        </p>
      </div>

      {/*
        One line, not three. The mechanism's own rules — one redemption per
        organization, one per mailbox per campaign — are in OPERATIONS; the only
        one worth repeating here is what this credit can be turned into, because
        that is what decides whether the amount above can buy the product the
        platform pays a supplier for.

        It said "never AI inference" as a fixed string, which was true of every
        campaign until the checkbox above existed and was still sitting there
        contradicting it afterwards — under a preview, in the smallest type on
        the form, which is exactly where a stale sentence survives.
      */}
      <p className="text-xs text-fg-subtle">
        {aiSpendable
          ? "Apps, databases and AI inference. Opens AI for these customers without a top-up."
          : "Apps and databases only — never AI inference."}
      </p>

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button loading={pending} onClick={() => void submit()}>
          {campaign ? "Save changes" : "Create campaign"}
        </Button>
      </div>
    </div>
  );
}

/** Micros to a currency string for an input, or empty. */
function units(micros: number | null | undefined): string {
  return micros === null || micros === undefined ? "" : (micros / 1_000_000).toFixed(2);
}

/** A currency string to micros. Rounded, so a typed value cannot land fractional. */
function micros(value: string): number {
  return Math.round(Number(value) * 1_000_000);
}
